import fs from 'node:fs'
import path from 'node:path'
import type {
  NextConfigScaffoldOptions,
  NextConfigScaffoldResult
} from '@cli-types/next-adapter/init.js'
import { createNextConfigTemplate } from './create-next-config-template.js'
import { ensureBetterStartImageHost } from './ensure-better-start-image-host.js'
import { ensureCacheComponents } from './ensure-cache-components.js'
import { ensureExperimentalUseCache } from './ensure-experimental-use-cache.js'
import { ensurePartialPrefetching } from './ensure-partial-prefetching.js'
import { findConfigObject } from './find-config-object.js'
import { resolveCacheConfigMode } from './resolve-cache-config-mode.js'

export function scaffoldNextConfig({
  cwd,
  nextMajorVersion
}: NextConfigScaffoldOptions): NextConfigScaffoldResult {
  const cacheConfigMode = resolveCacheConfigMode(nextMajorVersion)
  if (!cacheConfigMode) {
    return { status: 'not-needed' }
  }

  const variants = ['next.config.ts', 'next.config.mjs', 'next.config.js']
  let configPath: string | null = null

  for (const variant of variants) {
    const candidate = path.resolve(cwd, variant)
    if (fs.existsSync(candidate)) {
      configPath = candidate
      break
    }
  }

  if (!configPath) {
    const newPath = path.resolve(cwd, 'next.config.ts')
    fs.writeFileSync(newPath, createNextConfigTemplate(cacheConfigMode), 'utf-8')
    return { status: 'created' }
  }

  const content = fs.readFileSync(configPath, 'utf-8')
  const configObject = findConfigObject(content)

  if (!configObject) {
    return { status: 'unsupported' }
  }

  const cacheUpdateResult =
    cacheConfigMode === 'cache-components'
      ? ensureCacheComponents(content, configObject)
      : ensureExperimentalUseCache(content, configObject)

  if (cacheUpdateResult.unsupported) {
    return { status: 'unsupported' }
  }

  let updatedContent = cacheUpdateResult.content
  let changed = cacheUpdateResult.changed

  if (cacheConfigMode === 'cache-components') {
    const cacheConfigObject = findConfigObject(updatedContent)
    if (!cacheConfigObject) {
      return { status: 'unsupported' }
    }

    const partialPrefetchingUpdateResult = ensurePartialPrefetching(
      updatedContent,
      cacheConfigObject
    )
    if (partialPrefetchingUpdateResult.unsupported) {
      return { status: 'unsupported' }
    }

    updatedContent = partialPrefetchingUpdateResult.content
    changed ||= partialPrefetchingUpdateResult.changed
  }

  const updatedConfigObject = findConfigObject(updatedContent)
  if (!updatedConfigObject) {
    return { status: 'unsupported' }
  }

  const imagesUpdateResult = ensureBetterStartImageHost(updatedContent, updatedConfigObject)
  if (imagesUpdateResult.unsupported) {
    return { status: 'unsupported' }
  }

  changed ||= imagesUpdateResult.changed
  if (!changed) {
    return { status: 'already-set' }
  }

  fs.writeFileSync(configPath, imagesUpdateResult.content, 'utf-8')
  return { status: 'updated' }
}
